import { Language, useLanguageStore } from "~/stores/language-store";

const EMBED_PARENT_ORIGIN_KEY = "embedParentOrigin";
const EMBED_PARENT_URL_KEY = "embedParentUrl";
// Must match the hash key that public/embed.js reads to reopen the drawer.
const REOPEN_HASH_KEY = "kalia-booking";

const allowedParentOrigins = (import.meta.env.VITE_EMBED_PARENT_ORIGINS ?? "")
    .split(",")
    .map((origin) => origin.trim())
    .filter(Boolean);

const originOf = (url: string) => {
    try {
        return new URL(url).origin;
    } catch {
        return null;
    }
};

// Must run before the router renders: StoreSelection navigates away on ?store=, which drops the query string.
export const initEmbedMode = () => {
    if (window.self === window.top) {
        return;
    }
    const params = new URLSearchParams(window.location.search);
    const parentOrigin = params.get("parentOrigin");
    if (params.get("embed") === "1" && parentOrigin && allowedParentOrigins.includes(parentOrigin)) {
        sessionStorage.setItem(EMBED_PARENT_ORIGIN_KEY, parentOrigin);

        const parentUrl = params.get("parentUrl");
        if (parentUrl && originOf(parentUrl) === parentOrigin) {
            sessionStorage.setItem(EMBED_PARENT_URL_KEY, parentUrl);
        }

        const language = params.get("lang");
        if (language === "de" || language === "en") {
            useLanguageStore.getState().setLanguage(language === "en" ? Language.ENGLISH : Language.GERMAN);
        }
    }

    if (isEmbedded()) {
        window.addEventListener("keydown", closeOnEscape);
    }
};

// Radix closes its own open layers on Escape first; their DOM is still marked open at this point.
const OPEN_LAYER_SELECTOR = '[data-state="open"]:is([role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"])';

const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || event.defaultPrevented || document.querySelector(OPEN_LAYER_SELECTOR)) {
        return;
    }
    postToParent("close");
};

export const getEmbedParentOrigin = () => {
    if (window.self === window.top) {
        return null;
    }
    return sessionStorage.getItem(EMBED_PARENT_ORIGIN_KEY);
};

export const isEmbedded = () => getEmbedParentOrigin() !== null;

/** The website page the drawer is open on, e.g. https://www.kalialab.de/termin-buchen */
export const getEmbedParentUrl = () => (isEmbedded() ? sessionStorage.getItem(EMBED_PARENT_URL_KEY) : null);

export const isAllowedParentUrl = (url: string) => {
    const origin = originOf(url);
    return origin !== null && allowedParentOrigins.includes(origin);
};

/** URL of the website page that makes embed.js reopen the drawer at the given funnel path. */
export const buildDrawerReopenUrl = (parentUrl: string, funnelPath: string) => {
    const url = new URL(parentUrl);
    url.hash = `${REOPEN_HASH_KEY}=${encodeURIComponent(funnelPath)}`;
    return url.toString();
};

// Login (magic link in a new tab) and Klarna (full-page redirect) leave the drawer and come back to the
// standalone funnel. These entries live in localStorage, which the drawer and the standalone funnel only
// share when the website is same-site (www.kalialab.de); elsewhere they are simply never found.
const RETURN_PAGE_KEY = "embedReturnPage";
const RETURN_PAGE_MAX_AGE_MS = 60 * 60 * 1000;
const DRAWER_HANDOVER_KEY = "embedDrawerHandover";
const DRAWER_HANDOVER_MAX_AGE_MS = 10 * 60 * 1000;

const takeStoredEntry = <T extends { createdAt: number }>(key: string, maxAgeMs: number): T | null => {
    const raw = localStorage.getItem(key);
    if (!raw) {
        return null;
    }
    localStorage.removeItem(key);
    try {
        const entry = JSON.parse(raw) as T;
        return Date.now() - entry.createdAt <= maxAgeMs ? entry : null;
    } catch {
        return null;
    }
};

/** Call right before leaving the drawer, so the standalone funnel can send the customer back into it. */
export const rememberEmbedReturnPage = () => {
    const url = getEmbedParentUrl();
    if (url) {
        localStorage.setItem(RETURN_PAGE_KEY, JSON.stringify({ url, createdAt: Date.now() }));
    } else {
        localStorage.removeItem(RETURN_PAGE_KEY);
    }
};

/** The website page to return to, if the current standalone flow was started in the drawer. */
export const consumeEmbedReturnPage = () => {
    if (isEmbedded()) {
        return null;
    }
    const entry = takeStoredEntry<{ url: string; createdAt: number }>(RETURN_PAGE_KEY, RETURN_PAGE_MAX_AGE_MS);
    return entry && isAllowedParentUrl(entry.url) ? entry.url : null;
};

/** Reopens the drawer at funnelPath on the website page; data goes via storage to keep it out of website URLs. */
// Only for showing results in the drawer: the website may fail to reopen it, so nothing essential may depend on it.
export const handOverToDrawer = (returnPage: string, funnelPath: string, data: unknown) => {
    localStorage.setItem(DRAWER_HANDOVER_KEY, JSON.stringify({ path: funnelPath, data, createdAt: Date.now() }));
    window.location.replace(buildDrawerReopenUrl(returnPage, funnelPath));
};

export const takeDrawerHandover = <T>(funnelPath: string): T | null => {
    if (!isEmbedded()) {
        return null;
    }
    const entry = takeStoredEntry<{ path: string; data: T; createdAt: number }>(
        DRAWER_HANDOVER_KEY,
        DRAWER_HANDOVER_MAX_AGE_MS,
    );
    return entry?.path === funnelPath ? entry.data : null;
};

export const postToParent = (type: string, payload?: unknown) => {
    const parentOrigin = getEmbedParentOrigin();
    if (!parentOrigin) {
        return;
    }
    window.parent.postMessage({ source: "kalia-booking", type, payload }, parentOrigin);
};
